import React, { useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import { am4core, am4charts } from "../../../../../../loaders/amchartsLoader";
import { colors, statusColors } from "../../../../../../utils/colors";
import useI18n from '../../../../../../i18n/useI18n';
import {
  setZoom,
  setScrollbarStyling,
  addLegends,
  createRangeSetZoom,
  tooltipStyling,
  addTriangleBullet,
  addCursor,
  formatDateInAMPM,
  genericTtip,
  getSeriesColor,
  getMetricSeriesName
} from "../../common/adUtils";
import { exportCSV } from "../../../../../../utils/chart";
import { EXPORT_CSV_LABELS } from "../../../../../../utils/enums";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { shortDisplayTime } from "../../../../../../utils/displayTime";
import { initChartThemeSupport } from "../../../../../../common/Theme";


const [
  HbrIconButton,
  HbrTooltip
] = reactWrapper(["hbr-icon-button", "hbr-tooltip"]);
const ADUsageLineChart = props => {
  const { tViews } = useI18n('views')
  const currentChartRef = useRef();
  const [isScrollBar, setIsScrollBar] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [timeStampLabel, setTimeStampLabel] = useState();

  const addScrollBar = chart => {
    chart.events.on("ready", () => {
      var scrollBar = new am4charts.XYChartScrollbar();
      chart.scrollbarX = scrollBar;
      //To ensure scrollbar interaction handlers are attached on first load.
      initChartThemeSupport(chart, am4core, props);
      let series = chart.series.getIndex(0);
      if (series) scrollBar?.series?.push(series);
      chart.series.each(series => scrollBar?.series?.push(series));
      const scrollbarLabelPrefix =
        chart.yAxes.getIndex(0)?.title?.text || tViews('usageBasedAD.usageGb');
      setScrollbarStyling(scrollBar, { labelPrefix: scrollbarLabelPrefix });
      var scrollSeries = chart.scrollbarX?.scrollbarChart?.series?.getIndex(0);
      if (scrollSeries) scrollSeries.strokeDasharray = "2,4";
      chart.scrollbarX.parent = chart.bottomAxesContainer;
      setIsScrollBar(true);
    });
  };

  const addTooltip = (series, chart, isFirstSeries) => {
    if (!isFirstSeries && series) {
      return series;
    }

    series?.adapter.add("tooltipText", function(text, target) {
      let dataItem = target?.tooltipDataItem?.dataContext;
      const tooltipTimestamp =
        dataItem?.entry_ts && formatDateInAMPM(dataItem?.entry_ts);
      let tooltipContent = `${tViews('postSpaceContent' , {tooltip : `[bold]${tooltipTimestamp}[/]\n\n`})}`;
      return genericTtip(
        tooltipContent,
        dataItem,
        tViews,
        props.palette,
        props.circuitName,
        chart
      );
    });
    tooltipStyling(series?.tooltip);

    return series;
  };

  const createSeries = (chart, field, index) => {
    let series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.valueY = field;
    series.dataFields.dateX = "entry_ts";
    series.name = getMetricSeriesName(field);
    series.connect = false;
    series.autoGapCount = 1.1;
    series.strokeWidth = 2;
    series.showOnInit = false;
    let seriesColor =
      props.circuitName && props.circuitName === series.name
        ? colors.teal60
        : getSeriesColor(series.name, props.palette)?.[0]?.color || chart.colors.getIndex(index);
    series.fill = seriesColor;
    series.stroke = seriesColor;

    if (props.id === "app-usage-trend-chart") {
      series.fillOpacity = 0.2;
      series.stacked = true;
    }
    addTooltip(series, chart, index === 0);

    if (field === "Baseline") {
      series.fillOpacity = 0.2;
      series.fill = am4core.color(colors.gray80);
      series.stroke = am4core.color(colors.gray80);
      series.strokeDasharray = "4,2";
      series.strokeWidth = 4;
      series.strokeOpacity = 1;
    } else if (field === "Usage") {
      series.fill = colors.teal60;
      series.stroke = colors.teal60;
    }

    return series;
  };

  const createValueAxis = chart => {
    var valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.minGridDistance = 50;
    valueAxis.cursorTooltipEnabled = false;
    valueAxis.numberFormatter.numberFormat = "#.b";

    valueAxis.extraMin = 0;
    valueAxis.extraMax = 0.1; // 10% extra space at the top

    return valueAxis;
  };

  const createDataAxis = chart => {
    var dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.grid.template.location = 0;
    dateAxis.renderer.labels.template.location = 0.0001;
    dateAxis.startLocation = 0;
    dateAxis.endLocation = 1;
    dateAxis.baseInterval = { timeUnit: "hour", count: 1 };
    dateAxis.cursorTooltipEnabled = false;

    return dateAxis;
  };

  const createChart = (id, data) => {
    am4core.options.autoDispose = true;
    var chart = am4core.create(id, am4charts.XYChart);
    chart.data = data || [];
    chart.height = 300;
    chart.paddingLeft = -10;
    chart.paddingRight = 40;
    initChartThemeSupport(chart, am4core, props);
    return chart;
  };

  useEffect(() => {
    if (!props.data || !props.data.length) return;
    let chart = createChart(props.id, props.data);
    currentChartRef.current = chart;
    createDataAxis(chart);
    createValueAxis(chart);

    let keyList = props.data.map(d => {
      return Object.keys(d);
    });
    // converts 2D array to 1D array and remove duplicates.
    let uniqueKeys = [...new Set(keyList.flat())];
    let seriesIndex = 0;
    uniqueKeys.forEach(d => {
      if (
        d !== "entry_ts" &&
        !d.includes("anomaly") &&
        !d.includes("tTip") &&
        // This condition is to avoid adding series when addedCircuit is present
        (!props.addedCircuit || props?.addedCircuit?.length === 0)
      ) {
        createSeries(chart, d, seriesIndex);
        seriesIndex++;
      }
    });
    addScrollBar(chart);
    addLegends(chart, props, statusColors, colors);
    chart.legend.itemContainers.template.tooltipText = tViews('clickToToggle');
    chart.legend.itemContainers.template.tooltipPosition = "pointer";
    chart.legend.itemContainers.template.clickable = true;
    chart.legend.itemContainers.template.focusable = true;
    chart.legend.itemContainers.template.cursorOverStyle =
      am4core.MouseCursorStyle.pointer;

    addCursor(chart);
    setTimeStampLabel(
      shortDisplayTime(props.timePeriod?.[0]) +
        " - " +
        shortDisplayTime(props.timePeriod?.[1])
    );
    return () => {
      if (chart) {
        chart.dispose();
      }
    };
  }, [props.id, props.data]);

  useEffect(() => {
    setTimeout(() => {
      if (isScrollBar === true && props.selectedHour) {
        let chart = currentChartRef.current;

        // Adjust the scrollbar position according to selected hour
        let scrollRange = createRangeSetZoom(props, chart);
        setZoom(chart, scrollRange?.dateEnd, scrollRange?.dateStart);
        setIsZoomed(true);
      }
    }, 500); // Allow chart to render before applying zoom
  }, [isScrollBar, props.selectedHour]);

  useEffect(() => {
    if (isZoomed === true) {
      let chart = currentChartRef.current;
      if (props.id !== "app-usage-trend-chart")
        addTriangleBullet(chart, props.circuitName);
    }
  }, [isZoomed]);

  useEffect(() => {
    // Remove the series without chart re-rendering
    if (currentChartRef.current && props?.removedList?.length > 0) {
      // Remove series whose name or id matches a circuit in removedList
      props.removedList.forEach(name => {
        currentChartRef.current.series.each(series => {
          if (series) {
            if (series?.dataFields?.valueY === `${name}_usage`) {
              currentChartRef.current.series
                .removeIndex(currentChartRef.current.series.indexOf(series))
                .dispose();
            }
          }
        });
      });
      currentChartRef.current.series.each((series, index) => {
        addTooltip(series, currentChartRef.current, index === 0);
      });
      const yAxis = currentChartRef.current.yAxes.getIndex(0);
      if (yAxis) {
        yAxis.data = yAxis.data.filter(
          c => !props.removedList.includes(c.name)
        );
      }
    }
  }, [props.removedList]);

  useEffect(() => {
    // Re-draw the series without chart re-rendering
    if (props?.addedCircuit?.length > 0) {
      // let index = 0;
      // Add a LineSeries back
      props.addedCircuit.forEach(circuitAdded => {
        const newSeries = currentChartRef.current.series.push(
          new am4charts.LineSeries()
        );
        newSeries.data = props.data;
        newSeries.dataFields.valueY = `${circuitAdded}_usage`; // set according to your data structure
        newSeries.dataFields.dateX = "entry_ts"; // set according to your data structure
        newSeries.name = circuitAdded;

        let seriesColor =
          props.circuitName && props.circuitName === newSeries.name
            ? colors.teal60
            : getSeriesColor(newSeries.name, props.palette)?.[0]?.color;

        newSeries.fill = seriesColor;
        newSeries.stroke = seriesColor;

        // Optionally, configure appearance
        newSeries.strokeWidth = 2;
        let index = currentChartRef.current.series.indexOf(newSeries);
        if (props.id === "app-usage-trend-chart") {
          newSeries.fillOpacity = 0.2;
          newSeries.stacked = true;
        }

        // Add tooltip
        addTooltip(newSeries, currentChartRef.current, index === 0);
      });
    }
  }, [props.addedCircuit]);

  const onDownloadCsvClick = () => {
    if (currentChartRef.current && props.id) {
      const label = EXPORT_CSV_LABELS[props.id] || EXPORT_CSV_LABELS.AD_SUMMARY;
      exportCSV(currentChartRef.current, label);
    }
  };

  return (
    <div>
      <div id="usage-based-ad-download-csv">
        <HbrTooltip content={tViews('downloadCSV')}>
          <span>
            <HbrIconButton
              size="20px"
              name="file-csv"
              data-testid="DownloadCsvBtn"
              onClick={() => onDownloadCsvClick()}
              arialabel={
                tViews('downloadChartDisplaying') +
                props.id.toLowerCase() +
                tViews('downloadCSVInfo') +
                timeStampLabel
              }
            />
          </span>
        </HbrTooltip>
      </div>
      <div id={props.id} role="region" aria-label={props.id} tabIndex={0} />
    </div>
  );
};

ADUsageLineChart.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  selectedHour: PropTypes.string,
  anomaly_legend: PropTypes.bool,
  removedList: PropTypes.array,
  addedCircuit: PropTypes.string,
  palette: PropTypes.array,
  circuitName: PropTypes.string,
  timePeriod: PropTypes.array
};

export default ADUsageLineChart;
